<template>
  <li
    ref="tooltipAnchor"
    class="main-sidebar-item"
    :class="{ 'mb-n1': slotShown }"
  >
    <main-sidebar-button
      :route="route"
      class="main-sidebar-item__link"
      :class="{ 'main-sidebar-item__nested': nested }"
      :active="active"
      :skip-action="expandable && isMenuOpen"
      @click="$emit('toggle')"
    >
      <tm-icon
        v-if="icon"
        :name="icon"
        size="xLarge"
      />

      <template v-if="isMenuOpen">
        <div
          class="flex-grow-1 ml-3"
          :class="{ 'ml-6_5': !icon }"
        >
          {{ label }}
        </div>

        <slot name="badges" />

        <dropdown-direction
          v-if="expandable"
          :class="{ 'mr-4': !nested, 'mr-2': nested }"
          name="tmi-arrow-down"
          size="large"
          :opened="expanded"
        />

        <div
          v-else
          class="main-sidebar-item__direction-placeholder"
        />
      </template>

      <template v-else>
        <tm-tooltip
          position="right"
          delayed
          :offset="[mainSidebarTooltipOffset, 10]"
        >
          {{ label }}
        </tm-tooltip>

        <slot name="badges" />
      </template>

      <template v-if="showFeatureTooltip">
        <div
          ref="tooltipAnchor"
          class="main-sidebar-item__tooltip-anchor"
        />

        <component
          :is="routeMeta.featureTooltip.component"
          v-if="routeMeta?.featureTooltip"
          :target="tooltipAnchor"
          :config="routeMeta.featureTooltip"
          :is-menu-open="isMenuOpen"
        />
      </template>
    </main-sidebar-button>

    <div
      v-if="slotShown"
      class="mt-1"
    >
      <slot />
    </div>
  </li>
</template>

<script setup lang="ts">
import MainSidebarButton from '@/components/layout/mainSidebar/MainSidebarButton.vue'
import { isSidebarAlwaysExpandable } from '@/composition/sidebar/sidebar'
import TmIcon from '@/components/shared/TmIcon.vue'
import TmTooltip from '@/components/shared/tooltip/TmTooltip.vue'
import { mainSidebarTooltipOffset } from '@/components/layout/mainSidebar/types'
import type { TmNamedRoute } from '@/services/route/types'
import DropdownDirection from '@/components/shared/dropdowns/DropdownDirection.vue'
import { getRouterService } from '@/core/container/ioc'
import { computed, ref, useSlots } from '@/composition/vue/compositionApi'
import { useMenuState } from '@/composition/menuState'
import { useBreakpoints } from '@/composition/breakpoints'
import { useFeatureTooltipDisplay } from '@/composition/featureTooltip'
import type { IconName } from '@/assets/icons/icons'

const props = defineProps<{
  label: string
  icon?: IconName
  route?: TmNamedRoute
  mainSectionRoute?: TmNamedRoute | TmNamedRoute[]
  expanded?: boolean
  nested?: boolean
}>()

defineEmits<{
  toggle: []
}>()

const tooltipAnchor = ref<HTMLElement>()

const { isTablet } = useBreakpoints()
const { isMenuOpen } = useMenuState()

const routerService = getRouterService()

const routeMeta = computed(() => {
  if (!props.route) return null
  const route = routerService.getRouteByName(props.route.name)
  return route.meta || null
})

const active = computed(() => {
  const route = props.mainSectionRoute ?? props.route

  if (!route || expandable.value) {
    return false
  }

  if (Array.isArray(route)) {
    return route.some((r) => routerService.isActiveRouteByName(r.name))
  }

  return routerService.isActiveRouteByName(route.name)
})

const slots = useSlots()
const { alwaysExpandable } = isSidebarAlwaysExpandable()
const expandable = computed(() => {
  if (!slots.default) return false
  if (alwaysExpandable) return true
  return isTablet.value
})

const slotShown = computed(() => expandable.value && props.expanded && isMenuOpen.value)
const showFeatureTooltip = computed(() => {
  return (
    routeMeta.value?.featureTooltip &&
    useFeatureTooltipDisplay(routeMeta.value.featureTooltip).isFeatureTooltipDisplayed &&
    (isMenuOpen.value || !isTablet.value)
  )
})
</script>

<style lang="scss" scoped>
.main-sidebar-item {
  list-style: none;
  padding: 2px 8px;
  white-space: nowrap;
  overflow: hidden;

  &__link {
    position: relative;
    height: 40px;
  }

  &__direction-placeholder {
    width: 16px;
  }

  &__tooltip-anchor {
    position: absolute;
    right: 3px;
    top: 12px;
  }

  &__nested {
    color: $gray5 !important;
  }
}

.ml-6_5 {
  margin-left: 26px !important;
}
</style>
